import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigableItem, useSuspendNavigationWhileOpen } from '../KeyboardNavigation';

type DsTrigger<T> = { open: () => void; close: () => void; ref: (node: T | null) => void };

/**
 * Wires a DS `Dropdown`'s trigger into arrow-key navigation, and closes it when Spotlight
 * closes (its menu renders in a portal, so Spotlight closing doesn't reach it on its own).
 *
 * `getStableTriggerRef({ open, close, ref })` must be called on every render with
 * Dropdown's trigger args, but always returns the *same* ref function — a new one each
 * render would make React re-attach it each render too, which loops with floating-ui's
 * own ref-driven state.
 */
export function useDropdownNavigableTrigger<T extends HTMLElement>(
	isSpotlightOpen: boolean,
	isDisabled = false,
) {
	const [isOpen, setIsOpen] = useState(false);
	const latestDsTrigger = useRef<DsTrigger<T> | undefined>(undefined);
	const isDisabledNow = useRef(isDisabled);
	isDisabledNow.current = isDisabled;

	const { isActive, itemRef } = useNavigableItem<T>(() => latestDsTrigger.current?.open(), {
		afterSelect: 'none',
	});

	useSuspendNavigationWhileOpen(isOpen);

	useEffect(() => {
		if (!isSpotlightOpen) {
			latestDsTrigger.current?.close();
		}
	}, [isSpotlightOpen]);

	const mergedRef = useCallback((node: T | null) => {
		latestDsTrigger.current?.ref(node);
		itemRef.current = isDisabledNow.current ? null : node;
	}, []); // eslint-disable-line react-hooks/exhaustive-deps -- itemRef is a stable ref object

	const getStableTriggerRef = useCallback((dsTrigger: DsTrigger<T>) => {
		latestDsTrigger.current = dsTrigger;

		return mergedRef;
	}, []); // eslint-disable-line react-hooks/exhaustive-deps -- mergedRef's identity never changes

	return {
		isActive,
		onOpen: () => setIsOpen(true),
		onClose: () => setIsOpen(false),
		getStableTriggerRef,
	};
}
